import type React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import LoadingState from "./LoadingState";

type TranslationKeys = keyof typeof translations.en;

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  title: string;
  loading?: boolean;
  loadingMessage?: string;
  error?: string | null;
  validationErrors?: string[];
  submitDisabled?: boolean;
  submitText?: string;
  cancelText?: string;
  children: React.ReactNode;
  dialogTestId?: string;
}

const FormDialog: React.FC<FormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  loading = false,
  loadingMessage = "Loading...",
  error = null,
  validationErrors = [],
  submitDisabled = false,
  submitText = "Submit",
  cancelText = "Cancel",
  children,
  dialogTestId,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <LoadingState message={loadingMessage} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid={dialogTestId}>
      <form onSubmit={onSubmit}>
        <DialogTitle>{t(title as TranslationKeys)}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                {validationErrors.map((error) => (
                  <div key={error}>{t(error as TranslationKeys)}</div>
                ))}
              </Typography>
            </Alert>
          )}

          {children}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {t(cancelText as TranslationKeys)}
          </Button>
          <Button type="submit" variant="contained" disabled={loading || submitDisabled}>
            {loading ? <CircularProgress size={24} /> : t(submitText as TranslationKeys)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FormDialog;
